"use client";

import { useState } from "react";
import { ArrowRight, Play, X, BookOpen } from "lucide-react";
import { images } from "@/config/images";
import Image from "next/image";
import Link from "next/link";
import { Headline2, Headline3 } from "@/components/typography";

export function HomeLearnSection() {
  const [selectedStory, setSelectedStory] = useState<string | null>(null);

  return (
    <section className="w-full bg-white py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Title */}
        <Headline2 className="text-spicenet-black mb-12">
          Learn with Spicenet
        </Headline2>

        {/* Blog Section */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <Headline3 className="text-spicenet-black">Blog</Headline3>
            <a
              href="https://spicenet.io/blogs"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-spicenet-black hover:text-spicenet-primary transition-colors duration-200"
            >
              <ArrowRight className="w-6 h-6" strokeWidth={2} />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {images.blog.map((post, index) => (
              <a
                key={index}
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group border border-spicenet-gray-light"
              >
                <article>
                  <div className="relative aspect-[1200/630] bg-spicenet-ash overflow-hidden">
                    <Image
                      src={post.image}
                      alt={post.title}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-lg font-medium tracking-[-0.5px] text-spicenet-black line-clamp-2 min-h-[3.5rem] mb-3">
                      {post.title}
                    </h4>

                    {/* Metadata */}
                    <div className="flex flex-col gap-2">
                      <div className="inline-flex items-center gap-2 bg-spicenet-primary/10 text-spicenet-primary rounded-full px-3 py-1.5 self-start">
                        <BookOpen className="w-4 h-4" strokeWidth={2} />
                        <span className="text-xs font-mono font-medium uppercase">
                          {post.readTime}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 font-mono uppercase">
                        BY {post.author} • {post.publishDate}
                      </span>
                    </div>
                  </div>
                </article>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* YouTube Story Modal */}
      {selectedStory && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedStory(null)}
        >
          <div
            className="relative w-full max-w-md aspect-[9/16] bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedStory(null)}
              className="absolute -top-10 md:-top-12 right-0 text-white hover:text-spicenet-primary transition-colors z-10 bg-black/50 p-2 rounded-full"
              aria-label="Close video"
            >
              <X className="w-6 h-6 md:w-8 md:h-8" />
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${selectedStory}?autoplay=1&controls=1&rel=0&modestbranding=1`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}
